import type {
  Attempt,
  FactId,
  StrategyEvidence,
  StrategyId,
  SchedulingState,
} from "@oefenheld/reken-core";
import {
  validateLearningCommit,
  validateStrategyLearningCommit,
  validateSchedulingCommit,
  type LearningRepository,
} from "./repository.ts";
import type {
  AchievementUnlock,
  ItemPresentation,
  LegacyImportRecord,
  LearnerProfile,
  LearningCommit,
  SelectorDecisionRecord,
  SessionRecord,
  StoredFactMastery,
  StoredSkillMastery,
  StoredStrategyMastery,
  StrategyLearningCommit,
  SchedulingCommit,
} from "./schema.ts";
import { withProfileLock } from "./profile-lock.ts";
import { recoverPresentationMarkers } from "./presentation-recovery.ts";

export const REKENHELD_DATABASE_NAME = "oefenheld-learning";
export const REKENHELD_DATABASE_VERSION = 2;

export const STORE_NAMES = {
  SCHEDULING: "scheduling",
  META: "meta",
  PROFILES: "profiles",
  SESSIONS: "sessions",
  SELECTOR_DECISIONS: "selectorDecisions",
  PRESENTATIONS: "presentations",
  ATTEMPTS: "attempts",
  STRATEGY_EVIDENCE: "strategyEvidence",
  FACT_MASTERY: "factMastery",
  STRATEGY_MASTERY: "strategyMastery",
  SKILL_MASTERY: "skillMastery",
  ACHIEVEMENT_UNLOCKS: "achievementUnlocks",
  LEGACY_IMPORTS: "legacyImports",
} as const;

type StoreName = (typeof STORE_NAMES)[keyof typeof STORE_NAMES];

function requestResult<T>(request: IDBRequest<T>): Promise<T> {
  return new Promise((resolve, reject) => {
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error ?? new Error("IndexedDB request mislukt."));
  });
}

function transactionDone(transaction: IDBTransaction): Promise<void> {
  return new Promise((resolve, reject) => {
    transaction.oncomplete = () => resolve();
    transaction.onabort = () => reject(
      transaction.error ?? new Error("IndexedDB-transactie is afgebroken."),
    );
    transaction.onerror = () => reject(
      transaction.error ?? new Error("IndexedDB-transactie is mislukt."),
    );
  });
}

function createStore(
  database: IDBDatabase,
  name: StoreName,
  keyPath: string,
): IDBObjectStore {
  return database.createObjectStore(name, { keyPath });
}

function migrateToVersion1(database: IDBDatabase): void {
  createStore(database, STORE_NAMES.META, "key");

  const profiles = createStore(database, STORE_NAMES.PROFILES, "id");
  profiles.createIndex("by_updated_at", "updatedAt");

  const sessions = createStore(database, STORE_NAMES.SESSIONS, "id");
  sessions.createIndex("by_profile", "profileId");
  sessions.createIndex("by_profile_local_date", ["profileId", "localDate"]);

  const decisions = createStore(database, STORE_NAMES.SELECTOR_DECISIONS, "id");
  decisions.createIndex("by_session", "sessionId");

  const presentations = createStore(database, STORE_NAMES.PRESENTATIONS, "id");
  presentations.createIndex("by_session", "sessionId");
  presentations.createIndex("by_session_state", ["sessionId", "state"]);

  const attempts = createStore(database, STORE_NAMES.ATTEMPTS, "id");
  attempts.createIndex("by_profile", "profileId");
  attempts.createIndex("by_profile_fact", ["profileId", "factId"]);
  attempts.createIndex("by_session", "sessionId");

  const strategyEvidence = createStore(database, STORE_NAMES.STRATEGY_EVIDENCE, "id");
  strategyEvidence.createIndex("by_profile_strategy", ["profileId", "strategyId"]);

  const factMastery = createStore(database, STORE_NAMES.FACT_MASTERY, "key");
  factMastery.createIndex("by_profile", "profileId");

  const strategyMastery = createStore(database, STORE_NAMES.STRATEGY_MASTERY, "key");
  strategyMastery.createIndex("by_profile", "profileId");

  const skillMastery = createStore(database, STORE_NAMES.SKILL_MASTERY, "key");
  skillMastery.createIndex("by_profile", "profileId");

  const unlocks = createStore(database, STORE_NAMES.ACHIEVEMENT_UNLOCKS, "key");
  unlocks.createIndex("by_profile", "profileId");

  const legacyImports = createStore(database, STORE_NAMES.LEGACY_IMPORTS, "id");
  legacyImports.createIndex("by_profile", "profileId");
}

export function applyDatabaseMigrations(
  database: IDBDatabase,
  transaction: IDBTransaction,
  oldVersion: number,
  newVersion: number,
): void {
  if (oldVersion < 1 && newVersion >= 1) {
    migrateToVersion1(database);
    transaction.objectStore(STORE_NAMES.META).put({
      key: "schemaVersion",
      value: 1,
    });
  }
  if (oldVersion < 2 && newVersion >= 2) {
    createStore(database, STORE_NAMES.SCHEDULING, "profileId");
    transaction.objectStore(STORE_NAMES.PRESENTATIONS).createIndex("by_profile", "profileId");
    transaction.objectStore(STORE_NAMES.META).put({ key: "schemaVersion", value: 2 });
  }
}

export interface OpenDatabaseOptions {
  readonly name?: string;
  readonly factory?: IDBFactory;
}

export function openRekenheldDatabase(
  options: OpenDatabaseOptions = {},
): Promise<IDBDatabase> {
  const factory = options.factory ?? globalThis.indexedDB;
  if (!factory) {
    return Promise.reject(new Error("IndexedDB is niet beschikbaar in deze omgeving."));
  }

  return new Promise((resolve, reject) => {
    const request = factory.open(
      options.name ?? REKENHELD_DATABASE_NAME,
      REKENHELD_DATABASE_VERSION,
    );
    request.onupgradeneeded = (event) => {
      applyDatabaseMigrations(
        request.result,
        request.transaction!,
        event.oldVersion,
        event.newVersion ?? REKENHELD_DATABASE_VERSION,
      );
    };
    request.onsuccess = () => {
      request.result.onversionchange = () => request.result.close();
      resolve(request.result);
    };
    request.onerror = () => reject(request.error ?? new Error("IndexedDB openen mislukt."));
    request.onblocked = () => reject(new Error("IndexedDB-upgrade is geblokkeerd door een oud tabblad."));
  });
}

function compareIso(a: string, b: string): number {
  return Date.parse(a) - Date.parse(b);
}

export class IndexedDbLearningRepository implements LearningRepository {
  readonly database: IDBDatabase;
  withProfileLock<T>(profileId: string, action: () => Promise<T>): Promise<T> {
    return withProfileLock(`rekenheld:${this.database.name}:${profileId}`, action);
  }
  getScheduling(profileId: string): Promise<SchedulingState | null> {
    return this.get(STORE_NAMES.SCHEDULING, profileId);
  }
  async commitScheduling(commit: SchedulingCommit): Promise<void> {
    await this.atomic([STORE_NAMES.SCHEDULING], (transaction) => this.writeScheduling(transaction, commit, commit.state.profileId));
  }
  private async writeScheduling(transaction: IDBTransaction, commit: SchedulingCommit | undefined, profileId: string): Promise<void> {
    if (!commit) return;
    const store = transaction.objectStore(STORE_NAMES.SCHEDULING);
    const current = await requestResult(store.get(profileId)) as SchedulingState | undefined;
    validateSchedulingCommit(current ?? null, commit, profileId);
    store.put(commit.state);
  }
  async commitProgress(presentation: ItemPresentation, scheduling: SchedulingCommit): Promise<void> {
    await this.atomic([STORE_NAMES.PRESENTATIONS, STORE_NAMES.SCHEDULING], async (transaction) => {
      const store = transaction.objectStore(STORE_NAMES.PRESENTATIONS);
      const current = await requestResult(store.get(presentation.id)) as ItemPresentation | undefined;
      if (current?.state !== "PRESENTED") throw new Error("Presentatie is niet open.");
      await this.writeScheduling(transaction, scheduling, presentation.profileId);
      store.put(presentation);
    });
  }
  async listPresentations(profileId: string): Promise<readonly ItemPresentation[]> {
    return (await this.listFromIndex<ItemPresentation>(STORE_NAMES.PRESENTATIONS, "by_profile", profileId))
      .sort((a, b) => compareIso(a.presentedAt, b.presentedAt));
  }
  async reconcilePresentations(profileId: string, at: string): Promise<void> {
    await this.atomic([STORE_NAMES.PRESENTATIONS, STORE_NAMES.SESSIONS, STORE_NAMES.ATTEMPTS, STORE_NAMES.STRATEGY_EVIDENCE], async (transaction) => {
      const presentations = transaction.objectStore(STORE_NAMES.PRESENTATIONS);
      const [items, sessions, attempts, evidence] = await Promise.all([
        requestResult(presentations.index("by_profile").getAll(profileId)),
        requestResult(transaction.objectStore(STORE_NAMES.SESSIONS).index("by_profile").getAll(profileId)),
        requestResult(transaction.objectStore(STORE_NAMES.ATTEMPTS).index("by_profile").getAll(profileId)),
        requestResult(transaction.objectStore(STORE_NAMES.STRATEGY_EVIDENCE).getAll()),
      ]);
      const updates = recoverPresentationMarkers(items as ItemPresentation[], sessions as SessionRecord[], attempts as Attempt[],
        (evidence as StrategyEvidence[]).filter((e) => e.profileId === profileId), at);
      for (const presentation of updates) presentations.put(presentation);
    });
  }

  private constructor(database: IDBDatabase) {
    this.database = database;
  }

  static async open(options: OpenDatabaseOptions = {}): Promise<IndexedDbLearningRepository> {
    return new IndexedDbLearningRepository(await openRekenheldDatabase(options));
  }

  close(): void {
    this.database.close();
  }

  private async atomic(
    stores: readonly StoreName[],
    write: (transaction: IDBTransaction) => Promise<void>,
  ): Promise<void> {
    const transaction = this.database.transaction([...stores], "readwrite");
    // Register completion before the first request, including synchronous clone failures.
    const completion = transactionDone(transaction).then(() => null, (error: unknown) => error);
    try {
      await write(transaction);
    } catch (error) {
      try { transaction.abort(); } catch { /* Already aborted or completed. */ }
      await completion;
      throw error;
    }
    const error = await completion;
    if (error) throw error;
  }

  private async appendStrategyEvidence(
    transaction: IDBTransaction,
    items: readonly StrategyEvidence[],
  ): Promise<void> {
    const store = transaction.objectStore(STORE_NAMES.STRATEGY_EVIDENCE);
    for (const item of items) {
      const existing = await requestResult(store.get(item.id));
      if (existing !== undefined && JSON.stringify(existing) !== JSON.stringify(item)) {
        throw new Error(`Strategy evidence-ID ${item.id} bestaat al met andere inhoud.`);
      }
      if (existing === undefined) store.add(item);
    }
  }

  private async get<T>(storeName: StoreName, key: IDBValidKey): Promise<T | null> {
    const transaction = this.database.transaction(storeName, "readonly");
    const value = await requestResult(transaction.objectStore(storeName).get(key));
    await transactionDone(transaction);
    return (value as T | undefined) ?? null;
  }

  private async put<T>(storeName: StoreName, value: T): Promise<void> {
    const transaction = this.database.transaction(storeName, "readwrite");
    transaction.objectStore(storeName).put(value);
    await transactionDone(transaction);
  }

  private async listFromIndex<T>(
    storeName: StoreName,
    indexName: string,
    key: IDBValidKey | IDBKeyRange,
  ): Promise<T[]> {
    const transaction = this.database.transaction(storeName, "readonly");
    const values = await requestResult(
      transaction.objectStore(storeName).index(indexName).getAll(key),
    );
    await transactionDone(transaction);
    return values as T[];
  }

  getProfile(profileId: string): Promise<LearnerProfile | null> {
    return this.get(STORE_NAMES.PROFILES, profileId);
  }

  putProfile(profile: LearnerProfile): Promise<void> {
    return this.put(STORE_NAMES.PROFILES, profile);
  }

  getSession(sessionId: string): Promise<SessionRecord | null> {
    return this.get(STORE_NAMES.SESSIONS, sessionId);
  }

  putSession(session: SessionRecord): Promise<void> {
    return this.put(STORE_NAMES.SESSIONS, session);
  }

  async closeSession(
    session: SessionRecord,
    abandonedPresentations: readonly ItemPresentation[],
  ): Promise<void> {
    for (const presentation of abandonedPresentations) {
      if (presentation.sessionId !== session.id || presentation.state !== "ABANDONED") {
        throw new Error("Afgebroken presentatie hoort niet bij de gesloten sessie.");
      }
    }
    const transaction = this.database.transaction([
      STORE_NAMES.SESSIONS,
      STORE_NAMES.PRESENTATIONS,
    ], "readwrite");
    for (const presentation of abandonedPresentations) {
      transaction.objectStore(STORE_NAMES.PRESENTATIONS).put(presentation);
    }
    transaction.objectStore(STORE_NAMES.SESSIONS).put(session);
    await transactionDone(transaction);
  }

  async listSessionsForProfile(profileId: string): Promise<readonly SessionRecord[]> {
    return (await this.listFromIndex<SessionRecord>(
      STORE_NAMES.SESSIONS,
      "by_profile",
      profileId,
    )).sort((a, b) => compareIso(a.startedAt, b.startedAt));
  }

  putSelectorDecision(decision: SelectorDecisionRecord): Promise<void> {
    return this.put(STORE_NAMES.SELECTOR_DECISIONS, decision);
  }

  getSelectorDecision(decisionId: string): Promise<SelectorDecisionRecord | null> {
    return this.get(STORE_NAMES.SELECTOR_DECISIONS, decisionId);
  }

  putPresentation(presentation: ItemPresentation): Promise<void> {
    return this.put(STORE_NAMES.PRESENTATIONS, presentation);
  }

  async commitPresentation(
    decision: SelectorDecisionRecord,
    presentation: ItemPresentation,
    scheduling?: SchedulingCommit,
  ): Promise<void> {
    if (
      decision.id !== presentation.selectionDecisionId
      || decision.sessionId !== presentation.sessionId
      || decision.profileId !== presentation.profileId
    ) {
      throw new Error("Selectorbesluit en presentatie horen niet bij elkaar.");
    }
    await this.atomic([STORE_NAMES.SELECTOR_DECISIONS, STORE_NAMES.PRESENTATIONS, STORE_NAMES.SCHEDULING], async (transaction) => {
      if (scheduling) {
        const presentations = await requestResult(transaction.objectStore(STORE_NAMES.PRESENTATIONS).index("by_profile").getAll(presentation.profileId)) as ItemPresentation[];
        if (presentations.some((p) => p.state === "PRESENTED")) throw new Error("Er staat al een presentatie open voor dit profiel.");
      }
      await this.writeScheduling(transaction, scheduling, presentation.profileId);
      transaction.objectStore(STORE_NAMES.SELECTOR_DECISIONS).put(decision);
      transaction.objectStore(STORE_NAMES.PRESENTATIONS).put(presentation);
    });
  }

  getPresentation(presentationId: string): Promise<ItemPresentation | null> {
    return this.get(STORE_NAMES.PRESENTATIONS, presentationId);
  }

  async listOpenPresentationsForSession(
    sessionId: string,
  ): Promise<readonly ItemPresentation[]> {
    return (await this.listFromIndex<ItemPresentation>(
      STORE_NAMES.PRESENTATIONS,
      "by_session_state",
      [sessionId, "PRESENTED"],
    )).sort((a, b) => compareIso(a.presentedAt, b.presentedAt));
  }

  async listAttempts(profileId: string): Promise<readonly Attempt[]> {
    return (await this.listFromIndex<Attempt>(
      STORE_NAMES.ATTEMPTS,
      "by_profile",
      profileId,
    )).sort((a, b) => compareIso(a.submittedAt, b.submittedAt));
  }

  async listAttemptsForFact(
    profileId: string,
    factId: FactId,
  ): Promise<readonly Attempt[]> {
    return (await this.listFromIndex<Attempt>(
      STORE_NAMES.ATTEMPTS,
      "by_profile_fact",
      [profileId, factId],
    )).sort((a, b) => compareIso(a.submittedAt, b.submittedAt));
  }

  async listAttemptsForSession(sessionId: string): Promise<readonly Attempt[]> {
    return (await this.listFromIndex<Attempt>(
      STORE_NAMES.ATTEMPTS,
      "by_session",
      sessionId,
    )).sort((a, b) => compareIso(a.submittedAt, b.submittedAt));
  }

  async listStrategyEvidence(
    profileId: string,
    strategyId: StrategyId,
  ): Promise<readonly StrategyEvidence[]> {
    return (await this.listFromIndex<StrategyEvidence>(
      STORE_NAMES.STRATEGY_EVIDENCE,
      "by_profile_strategy",
      [profileId, strategyId],
    )).sort((a, b) => compareIso(a.occurredAt, b.occurredAt));
  }

  getFactMastery(profileId: string, factId: FactId): Promise<StoredFactMastery | null> {
    return this.get(STORE_NAMES.FACT_MASTERY, `${profileId}::${factId}`);
  }

  async listFactMastery(profileId: string): Promise<readonly StoredFactMastery[]> {
    return (await this.listFromIndex<StoredFactMastery>(
      STORE_NAMES.FACT_MASTERY,
      "by_profile",
      profileId,
    )).sort((a, b) => a.factId.localeCompare(b.factId));
  }

  getStrategyMastery(
    profileId: string,
    strategyId: StrategyId,
  ): Promise<StoredStrategyMastery | null> {
    return this.get(STORE_NAMES.STRATEGY_MASTERY, `${profileId}::${strategyId}`);
  }

  async listStrategyMastery(profileId: string): Promise<readonly StoredStrategyMastery[]> {
    return (await this.listFromIndex<StoredStrategyMastery>(
      STORE_NAMES.STRATEGY_MASTERY,
      "by_profile",
      profileId,
    )).sort((a, b) => a.strategyId.localeCompare(b.strategyId));
  }

  getSkillMastery(
    profileId: string,
    skillId: string,
  ): Promise<StoredSkillMastery | null> {
    return this.get(STORE_NAMES.SKILL_MASTERY, `${profileId}::${skillId}`);
  }

  async listSkillMastery(profileId: string): Promise<readonly StoredSkillMastery[]> {
    return (await this.listFromIndex<StoredSkillMastery>(
      STORE_NAMES.SKILL_MASTERY,
      "by_profile",
      profileId,
    )).sort((a, b) => a.skillId.localeCompare(b.skillId));
  }

  async listAchievementUnlocks(profileId: string): Promise<readonly AchievementUnlock[]> {
    return (await this.listFromIndex<AchievementUnlock>(
      STORE_NAMES.ACHIEVEMENT_UNLOCKS,
      "by_profile",
      profileId,
    )).sort((a, b) => compareIso(a.unlockedAt, b.unlockedAt));
  }

  async putLegacyImport(record: LegacyImportRecord): Promise<void> {
    await this.atomic([STORE_NAMES.LEGACY_IMPORTS], async (transaction) => {
      const store = transaction.objectStore(STORE_NAMES.LEGACY_IMPORTS);
      const existing = await requestResult(store.get(record.id));
      if (existing && JSON.stringify(existing) !== JSON.stringify(record)) {
        throw new Error(`Legacy import-ID ${record.id} bestaat al met andere inhoud.`);
      }
      if (existing === undefined) store.add(record);
    });
  }

  async listLegacyImports(profileId: string): Promise<readonly LegacyImportRecord[]> {
    return (await this.listFromIndex<LegacyImportRecord>(
      STORE_NAMES.LEGACY_IMPORTS,
      "by_profile",
      profileId,
    )).sort((a, b) => compareIso(a.importedAt, b.importedAt));
  }

  async commitLearningResult(commit: LearningCommit): Promise<void> {
    validateLearningCommit(commit);
    const storeNames: StoreName[] = [
      STORE_NAMES.SCHEDULING,
      STORE_NAMES.ATTEMPTS,
      STORE_NAMES.PRESENTATIONS,
      STORE_NAMES.FACT_MASTERY,
      STORE_NAMES.STRATEGY_EVIDENCE,
      STORE_NAMES.STRATEGY_MASTERY,
      STORE_NAMES.SKILL_MASTERY,
      STORE_NAMES.ACHIEVEMENT_UNLOCKS,
      STORE_NAMES.SESSIONS,
    ];
    await this.atomic(storeNames, async (transaction) => {
      const attempts = transaction.objectStore(STORE_NAMES.ATTEMPTS);
      const existingAttempt = await requestResult(attempts.get(commit.attempt.id)) as Attempt | undefined;
      if (existingAttempt) {
        if (JSON.stringify(existingAttempt) !== JSON.stringify(commit.attempt)) {
          throw new Error(`Attempt-ID ${commit.attempt.id} bestaat al met andere inhoud.`);
        }
        return;
      }

      const storedPresentation = await requestResult(transaction.objectStore(STORE_NAMES.PRESENTATIONS).get(commit.presentation.id)) as ItemPresentation | undefined;
      if (storedPresentation && storedPresentation.state !== "PRESENTED") {
        throw new Error("Presentatie is al afgesloten; een tweede antwoord is niet toegestaan.");
      }

      await this.writeScheduling(transaction, commit.scheduling, commit.session.profileId);
      attempts.add(commit.attempt);
      transaction.objectStore(STORE_NAMES.PRESENTATIONS).put(commit.presentation);
      transaction.objectStore(STORE_NAMES.FACT_MASTERY).put(commit.factMastery);
      await this.appendStrategyEvidence(transaction, commit.strategyEvidence);
      for (const mastery of commit.strategyMastery) {
        transaction.objectStore(STORE_NAMES.STRATEGY_MASTERY).put(mastery);
      }
      for (const mastery of commit.skillMastery) {
        transaction.objectStore(STORE_NAMES.SKILL_MASTERY).put(mastery);
      }
      const unlockStore = transaction.objectStore(STORE_NAMES.ACHIEVEMENT_UNLOCKS);
      for (const unlock of commit.achievementUnlocks) {
        const existing = await requestResult(unlockStore.get(unlock.key));
        if (existing === undefined) unlockStore.add(unlock);
      }
      transaction.objectStore(STORE_NAMES.SESSIONS).put(commit.session);
    });
  }

  async commitStrategyLearning(commit: StrategyLearningCommit): Promise<void> {
    validateStrategyLearningCommit(commit);
    await this.atomic([
      STORE_NAMES.SCHEDULING,
      STORE_NAMES.PRESENTATIONS,
      STORE_NAMES.STRATEGY_EVIDENCE,
      STORE_NAMES.STRATEGY_MASTERY,
      STORE_NAMES.SKILL_MASTERY,
      STORE_NAMES.ACHIEVEMENT_UNLOCKS,
      STORE_NAMES.SESSIONS,
    ], async (transaction) => {
      const storedPresentation = await requestResult(transaction.objectStore(STORE_NAMES.PRESENTATIONS).get(commit.presentation.id)) as ItemPresentation | undefined;
      if (storedPresentation && storedPresentation.state !== "PRESENTED") {
        const storedEvidence = await Promise.all(commit.strategyEvidence.map((evidence) =>
          requestResult(transaction.objectStore(STORE_NAMES.STRATEGY_EVIDENCE).get(evidence.id))
        ));
        if (JSON.stringify(storedPresentation) === JSON.stringify(commit.presentation)
          && storedEvidence.every((evidence, index) => JSON.stringify(evidence) === JSON.stringify(commit.strategyEvidence[index]))) return;
        throw new Error("Presentatie is al afgesloten; een tweede lesresultaat is niet toegestaan.");
      }
      await this.writeScheduling(transaction, commit.scheduling, commit.session.profileId);
      transaction.objectStore(STORE_NAMES.PRESENTATIONS).put(commit.presentation);
      await this.appendStrategyEvidence(transaction, commit.strategyEvidence);
      transaction.objectStore(STORE_NAMES.STRATEGY_MASTERY).put(commit.strategyMastery);
      for (const mastery of commit.skillMastery) {
        transaction.objectStore(STORE_NAMES.SKILL_MASTERY).put(mastery);
      }
      const unlockStore = transaction.objectStore(STORE_NAMES.ACHIEVEMENT_UNLOCKS);
      for (const unlock of commit.achievementUnlocks) {
        const existing = await requestResult(unlockStore.get(unlock.key));
        if (existing === undefined) unlockStore.add(unlock);
      }
      transaction.objectStore(STORE_NAMES.SESSIONS).put(commit.session);
    });
  }
}
